import { useSyncExternalStore } from 'react';

/**
 * Where you came from, per tab (decided 2026-11-05): the installed app (PWA, `display: standalone`) has no browser Back
 * button, and record pages used to link to a fixed list ("← Quotes") whatever page you came from.
 *
 * This mirrors the tab's **in-app** history: every navigation is recorded by `NavHistoryTracker` (shell) with React
 * Router's location key and type (PUSH adds an entry after the current one, REPLACE swaps it, POP — Back / Forward /
 * reload — moves to the entry with that key). `DocumentTitle` names each entry with the page's title. Kept in
 * sessionStorage, so a reload keeps it and each window has its own.
 *
 * Readers: the header's Back button and `BackLink` (record pages) use `useBackTarget()`: the previous in-app page, or
 * null when there's none (a link opened fresh from Teams / an email), and go there with `navigate(-1)`, so the real
 * history stays the one source of truth.
 */

export interface NavEntry { key: string; path: string }
interface State { entries: NavEntry[]; index: number; titles: Record<string, string> }

const STORAGE_KEY = 'cosmos:nav:history';
const MAX = 50;

function load(): State {
  try {
    const s = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null') as State | null;
    if (s && Array.isArray(s.entries) && typeof s.index === 'number') return { entries: s.entries, index: s.index, titles: s.titles ?? {} };
  } catch { /* storage unavailable or corrupt: start fresh */ }
  return { entries: [], index: -1, titles: {} };
}

let state: State = load();
const listeners = new Set<() => void>();

function commit(next: State) {
  // Keep the newest MAX entries (and only their titles).
  if (next.entries.length > MAX) {
    const drop = next.entries.length - MAX;
    next = { ...next, entries: next.entries.slice(drop), index: Math.max(0, next.index - drop) };
  }
  const keep = new Set(next.entries.map((e) => e.key));
  next = { ...next, titles: Object.fromEntries(Object.entries(next.titles).filter(([k]) => keep.has(k))) };
  state = next;
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* private window: in memory only */ }
  listeners.forEach((l) => l());
}

/** Called by NavHistoryTracker on every location change. */
export function recordNavigation(key: string, path: string, type: 'PUSH' | 'REPLACE' | 'POP') {
  const entry = { key, path };
  const { entries, index } = state;
  if (type === 'PUSH' && index >= 0) {
    commit({ ...state, entries: [...entries.slice(0, index + 1), entry], index: index + 1 });
  } else if (type === 'REPLACE' && index >= 0) {
    commit({ ...state, entries: entries.map((e, i) => (i === index ? entry : e)) });
  } else {
    // POP (Back / Forward / reload) to an entry we know, else a fresh start (first page of the tab, or history we
    // never saw): this page is the first one.
    const at = entries.findIndex((e) => e.key === key);
    if (at >= 0) commit({ ...state, entries: entries.map((e, i) => (i === at ? entry : e)), index: at });
    else commit({ ...state, entries: [entry], index: 0 });
  }
}

/** Called by DocumentTitle: the page's title for this history entry (record titles arrive once loaded). */
export function setEntryTitle(key: string, title: string) {
  if (state.titles[key] === title) return;
  commit({ ...state, titles: { ...state.titles, [key]: title } });
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
let cachedFor: State | null = null;
let cached: (NavEntry & { title: string | null }) | null = null;
function backSnapshot() {
  if (cachedFor !== state) {
    cachedFor = state;
    const prev = state.index > 0 ? state.entries[state.index - 1] : undefined;
    cached = prev ? { ...prev, title: state.titles[prev.key] ?? null } : null;
  }
  return cached;
}

/** The previous in-app page (go there with `navigate(-1)`), or null when this is the tab's first page. */
export function useBackTarget(): (NavEntry & { title: string | null }) | null {
  return useSyncExternalStore(subscribe, backSnapshot);
}
